import React, { useState } from 'react';
import { 
	Card, 
	Switch, 
	Select, 
	Button, 
	Typography, 
	Space, 
	message, 
	Modal,
	Alert,
	Tooltip 
} from 'antd';
import { 
	SafetyCertificateOutlined, 
	LockOutlined, 
	ClockCircleOutlined,
	SettingOutlined,
	InfoCircleOutlined,
	KeyOutlined,
	ApartmentOutlined,
	DeleteOutlined,
	CheckOutlined,
	CloseOutlined
} from '@ant-design/icons';
import PatternLock from '../pattern-lock/PatternLock';
import PasscodeLock from '../passcode-lock/PasscodeLock';
import { useSecurity } from '../../hooks/useSecurity';
import { useI18n } from '../../hooks/useI18n';
import './SecuritySettings.scss';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const SecuritySettings = () => {
	const {
		isEnabled,
		lockType,
		hasLockSet,
		inactivityTimeout,
		toggleSecurity,
		setLockType,
		updateInactivityTimeout,
		setNewLockPattern,
		setNewLockPasscode,
		removeLock,
		lockApp
	} = useSecurity();
	const { t } = useI18n();

	const [setupModalVisible, setSetupModalVisible] = useState(false);
	const [changeModalVisible, setChangeModalVisible] = useState(false);

	const handleSecurityToggle = (checked) => {
		if (checked && !hasLockSet) {
			// User wants to enable security but hasn't set up a lock
			message.warning(t('security.setup_lock_first'));
			setSetupModalVisible(true);
		} else if (!checked) {
			// User wants to disable security
			Modal.confirm({
				title: t('security.disable_security'),
				content: t('security.disable_security_confirm'),
				onOk: () => {
					toggleSecurity(false);
					message.success(t('security.security_disabled'));
				}
			});
		} else {
			toggleSecurity(checked);
		}
	};

	const handleLockTypeChange = (newType) => {
		if (hasLockSet) {
			Modal.confirm({
				title: t('security.change_lock_type'),
				content: t('security.change_lock_type_confirm'),
				onOk: () => {
					setLockType(newType);
					setSetupModalVisible(true);
					message.info(t('security.setup_new_lock'));
				}
			});
		} else {
			setLockType(newType);
		}
	};

	const handleSetupComplete = (value) => {
		let success = false;
		
		if (lockType === 'pattern') {
			success = setNewLockPattern(value);
		} else {
			success = setNewLockPasscode(value);
		}

		if (success) {
			setSetupModalVisible(false);
			toggleSecurity(true);
			// Don't show toast here as PatternLock/PasscodeLock already shows it
		} else {
			message.error(t('security.lock_setup_failed'));
		}
	};

	const handleChangeComplete = (value) => {
		let success = false;
		
		if (lockType === 'pattern') {
			success = setNewLockPattern(value);
		} else {
			success = setNewLockPasscode(value);
		}

		if (success) {
			setChangeModalVisible(false);
			// Don't show toast here as PatternLock/PasscodeLock already shows it
		} else {
			message.error(t('security.lock_change_failed'));
		}
	};

	const handleTestLock = () => {
		if (!isEnabled || !hasLockSet) {
			message.warning(t('security.setup_lock_first'));
			return;
		}
		
		lockApp();
		message.info(t('security.app_locked_test'));
	};

	const handleRemoveLock = () => {
		Modal.confirm({
			title: t('security.remove_lock'),
			content: t('security.remove_lock_confirm'),
			okType: 'danger',
			onOk: () => {
				const success = removeLock();
				if (success) {
					message.success(t('security.lock_removed_success'));
				}
			}
		});
	};

	const timeoutOptions = [
		{ value: 1, label: t('security.timeout_1_minute') },
		{ value: 2, label: t('security.timeout_2_minutes') },
		{ value: 5, label: t('security.timeout_5_minutes') },
		{ value: 10, label: t('security.timeout_10_minutes') },
		{ value: 15, label: t('security.timeout_15_minutes') },
		{ value: 30, label: t('security.timeout_30_minutes') },
		{ value: 60, label: t('security.timeout_1_hour') }
	];

	const currentTimeoutLabel = timeoutOptions.find((option) => option.value === inactivityTimeout)?.label;
	const currentLockTypeLabel = lockType === 'pattern' ? t('security.pattern_lock') : t('security.passcode_lock');
	const securityStateLabel = !hasLockSet
		? t('security.no_lock_set')
		: isEnabled
			? t('security.security_enabled')
			: t('security.disabled');
	const securityStateDescription = !hasLockSet
		? t('security.no_lock_set_description')
		: isEnabled
			? t('security.security_enabled_description', { timeout: inactivityTimeout, type: lockType })
			: t('security.enable_security_description');
	const canTestLock = isEnabled && hasLockSet;
	const modalIcon = lockType === 'pattern' ? <ApartmentOutlined /> : <KeyOutlined />;
	const modalSubtitle = lockType === 'pattern'
		? 'Use a gesture pattern to secure access.'
		: 'Use a numeric passcode to secure access.';

	const renderModalTitle = (title, subtitle) => (
		<div className="security-modal-title">
			<div className="security-modal-title__icon">{modalIcon}</div>
			<div className="security-modal-title__content">
				<div className="security-modal-title__heading">{title}</div>
				<div className="security-modal-title__subtitle">{subtitle}</div>
			</div>
		</div>
	);

	return (
		<div className="security-settings">
			<Card className="settings-card" bordered={false}>
				<div className="security-shell">
					<div className="security-hero">
						<div className="security-hero__content">
							<div className="security-hero__eyebrow">{t('security.security_title')}</div>
							<Title level={4}>
								<SafetyCertificateOutlined /> {t('security.security_title')}
							</Title>
							<Text type="secondary">
								{t('security.security_description')}
							</Text>
						</div>
						<div className="security-hero__stats">
							<div className="security-stat-card">
								<span>{t('security.enable_security')}</span>
								<strong>{securityStateLabel}</strong>
							</div>
							<div className="security-stat-card">
								<span>{t('security.lock_type')}</span>
								<strong>{currentLockTypeLabel}</strong>
							</div>
							<div className="security-stat-card">
								<span>{t('security.inactivity_timeout')}</span>
								<strong>{currentTimeoutLabel}</strong>
							</div>
						</div>
					</div>

					<div className="security-status-band">
						<Alert
							message={securityStateLabel}
							description={securityStateDescription}
							type={!hasLockSet ? 'warning' : isEnabled ? 'success' : 'info'}
							showIcon
							icon={!hasLockSet ? <InfoCircleOutlined /> : <SafetyCertificateOutlined />}
						/>
					</div>

					<div className="security-grid">
						<div className="security-panel security-panel--primary">
							<div className="setting-section setting-section--highlight">
								<div className="setting-header setting-header--split">
									<div>
										<Title level={5}>
											<LockOutlined /> {t('security.enable_security')}
										</Title>
										<Text type="secondary">
											{t('security.enable_security_description')}
										</Text>
									</div>
									<div className="security-status-pill">
										{isEnabled && hasLockSet ? t('security.enabled') : t('security.disabled')}
									</div>
								</div>
								<div className="setting-control setting-control--surface setting-control--toggle">
									<div>
										<div className="switch-label">{securityStateLabel}</div>
										<Text type="secondary">{hasLockSet ? currentLockTypeLabel : t('security.setup_lock_first')}</Text>
									</div>
									<Switch
										checked={isEnabled && hasLockSet}
										onChange={handleSecurityToggle}
										checkedChildren={<CheckOutlined />}
										unCheckedChildren={<CloseOutlined />}
										size="default"
									/>
								</div>
							</div>

							<div className="setting-section">
								<div className="setting-header setting-header--split">
									<div>
										<Title level={5}>
											<SettingOutlined /> {t('security.lock_type')}
										</Title>
										<Text type="secondary">
											{t('security.lock_type_description')}
										</Text>
									</div>
									<div className="selection-chip">{currentLockTypeLabel}</div>
								</div>
								<div className="setting-control">
									<Select
										value={lockType}
										onChange={handleLockTypeChange}
										className="security-select"
										disabled={!hasLockSet && isEnabled}
									>
										<Option value="pattern">
											<Space>
												<ApartmentOutlined />
												{t('security.pattern_lock')}
											</Space>
										</Option>
										<Option value="passcode">
											<Space>
												<KeyOutlined />
												{t('security.passcode_lock')}
											</Space>
										</Option>
									</Select>
								</div>
							</div>

							<div className="setting-section">
								<div className="setting-header setting-header--split">
									<div>
										<Title level={5}>
											<ClockCircleOutlined /> {t('security.inactivity_timeout')}
										</Title>
										<Text type="secondary">
											{t('security.inactivity_timeout_description')}
										</Text>
									</div>
									<div className="selection-chip">{currentTimeoutLabel}</div>
								</div>
								<div className="setting-control">
									<Select
										value={inactivityTimeout}
										onChange={updateInactivityTimeout}
										className="security-select"
										disabled={!hasLockSet}
									>
										{timeoutOptions.map((option) => (
											<Option key={option.value} value={option.value}>
												{option.label}
											</Option>
										))}
									</Select>
								</div>
							</div>
						</div>

						<div className="security-panel security-panel--secondary">
							<div className="setting-section">
								<div className="setting-header">
									<Title level={5}>{t('security.lock_management')}</Title>
									<Text type="secondary">
										{t('security.lock_management_description')}
									</Text>
								</div>
								<div className="action-grid">
									{!hasLockSet ? (
										<Button
											type="primary"
											icon={<LockOutlined />}
											onClick={() => setSetupModalVisible(true)}
											className="action-button action-button--primary"
										>
											{t('security.setup_lock')}
										</Button>
									) : (
										<>
											<Button
												icon={<SettingOutlined />}
												onClick={() => setChangeModalVisible(true)}
												className="action-button"
											>
												{t('security.change_lock')}
											</Button>
											<Tooltip title={t('security.test_lock_tooltip')}>
												<span className="action-button-wrapper">
													<Button
														icon={<LockOutlined />}
														onClick={handleTestLock}
														disabled={!canTestLock}
														className="action-button action-button--test"
													>
														{t('security.test_lock')}
													</Button>
												</span>
											</Tooltip>
											<Button
												danger
												icon={<DeleteOutlined />}
												onClick={handleRemoveLock}
												className="action-button action-button--danger"
											>
												{t('security.remove_lock')}
											</Button>
										</>
									)}
								</div>
							</div>

							<div className="security-info-card">
								<div className="security-info-card__header">
									<Text strong>{t('security.security_info')}</Text>
								</div>
								<ul className="security-info-list">
									<li>{t('security.info_automatic_lock')}</li>
									<li>{t('security.info_max_attempts')}</li>
									<li>{t('security.info_data_storage')}</li>
									<li>{t('security.info_bypass_warning')}</li>
								</ul>
							</div>
						</div>
					</div>
				</div>
			</Card>

			{/* Setup Lock Modal */}
			<Modal
				title={renderModalTitle(t('security.setup_lock_title'), modalSubtitle)}
				open={setupModalVisible}
				onCancel={() => setSetupModalVisible(false)}
				footer={null}
				width={lockType === 'pattern' ? 600 : 500}
				style={{ top: 20 }}
				wrapClassName={`security-settings-modal security-settings-modal--${lockType}`}
				destroyOnClose
			>
				{lockType === 'pattern' ? (
					<PatternLock
						isSetupMode
						isModal={true}
						onPatternComplete={handleSetupComplete}
						onCancel={() => setSetupModalVisible(false)}
						title="Set Up Security Lock"
						subtitle="Draw your pattern to secure the app"
					/>
				) : (
					<PasscodeLock
						isSetupMode
						isModal={true}
						onPasscodeComplete={handleSetupComplete}
						onCancel={() => setSetupModalVisible(false)}
						title="Set Up Security Lock"
						subtitle="Enter a passcode to secure the app"
					/>
				)}
			</Modal>

			{/* Change Lock Modal */}
			<Modal
				title={renderModalTitle(t('security.change_lock_title'), modalSubtitle)}
				open={changeModalVisible}
				onCancel={() => setChangeModalVisible(false)}
				footer={null}
				width={lockType === 'pattern' ? 600 : 500}
				style={{ top: 20 }}
				wrapClassName={`security-settings-modal security-settings-modal--${lockType}`}
				destroyOnClose
			>
				{lockType === 'pattern' ? (
					<PatternLock
						isSetupMode
						isModal={true}
						onPatternComplete={handleChangeComplete}
						onCancel={() => setChangeModalVisible(false)}
						title={t('security.change_pattern_title')}
						subtitle="Draw your new pattern"
					/>
				) : (
					<PasscodeLock
						isSetupMode
						isModal={true}
						onPasscodeComplete={handleChangeComplete}
						onCancel={() => setChangeModalVisible(false)}
						title={t('security.change_passcode_title')}
						subtitle="Enter your new passcode"
					/>
				)}
			</Modal>
		</div>
	);
};

export default SecuritySettings;